/**
 * /portal - authenticated subscriber portal shell (T-036, impl v10 Phase G).
 *
 * Replaces the Phase C (T-018) stub. This is now a TanStack layout route:
 * the `beforeLoad` guard enforces auth + active-subscriber + onboarding
 * complete (lib/portal-guard.ts, which itself reuses the Phase C
 * `requireSubscriberOrOperator` contract), and the component renders
 * `PortalLayout` with a child `<Outlet />`. Every /portal/* child route
 * inherits this guard and renders inside this shell.
 *
 * Per the original T-018 contract: unauthenticated access still redirects to
 * /auth/sign-in (delegated to requireSubscriberOrOperator inside
 * requirePortalEntry). The route-guard wiring is preserved, only widened
 * with the subscriber-state + onboarding-complete checks Phase G needs.
 *
 * `beforeLoad` returns the resolved `PortalEntryContext`; the component reads
 * it via `Route.useRouteContext()` so children do not re-fetch the subscriber
 * row just to know the tier.
 */

import { Outlet, createFileRoute } from '@tanstack/react-router';
import { requirePortalEntry } from '@/lib/portal-guard';
import { PortalLayout } from '@/components/portal/PortalLayout';
import { Toaster } from '@/components/ui/toast';

export const Route = createFileRoute('/portal')({
  beforeLoad: async ({ location }) => {
    const ctx = await requirePortalEntry({ redirectFrom: location.pathname });
    return { portal: ctx };
  },
  component: PortalShell,
});

function PortalShell() {
  const { portal } = Route.useRouteContext();
  return (
    <>
      <PortalLayout
        tier={portal.tier}
        isOperator={portal.isOperator}
        subscriberStatus={portal.subscriberStatus}
      >
        <Outlet />
      </PortalLayout>
      <Toaster position="bottom-right" richColors closeButton />
    </>
  );
}
